import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Skeleton,
  Paper,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useIntelligenceData } from '../../hooks/useIntelligenceData';
import MetricCard from '../common/MetricCard';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { formatCustomLabel, rangeDays, toYmd } from '../common/timeRange';
import AnomalyStrip from '../subscriptions/components/AnomalyStrip';
import PlanMixDonut from '../subscriptions/components/PlanMixDonut';
import GrowthTrendsTab from '../subscriptions/tabs/GrowthTrendsTab';
import ChurnDeepDiveTab from '../subscriptions/tabs/ChurnDeepDiveTab';
import CohortsExpandedTab from '../subscriptions/tabs/CohortsExpandedTab';
import UpgradePathsTab from '../subscriptions/tabs/UpgradePathsTab';
import DataSourceAttribution from '../common/DataSourceAttribution';
import { usePermissionAccess } from '../auth/RoleGuard';
import { formatCurrency } from '../../utils/currencyUtils';

type Segment = 'growth' | 'churn' | 'cohorts' | 'upgrades';

const SEGMENT_LABELS: Record<Segment, string> = {
  growth: 'Growth Trends',
  churn: 'Churn Deep-Dive',
  cohorts: 'Cohort Retention',
  upgrades: 'Upgrade Paths',
};

// ─── Section divider ──────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: '17px', fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11.5px', color: 'text.secondary', mt: 0.25 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ─── Net Revenue + costs card ─────────────────────────────────────────────────

const NetRevenueCostCard: React.FC<{
  netRevMTD: number;
  grokCostMTD: number;
  awsCostMTD: number;
  totalCostsMTD: number;
  loading?: boolean;
}> = ({ netRevMTD, grokCostMTD, awsCostMTD, totalCostsMTD, loading }) => {
  const valueColor = netRevMTD < 0 ? '#7D1935' : '#146152';
  return (
    <Card sx={{
      borderRadius: '16px',
      boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
      width: '100%',
      backgroundColor: 'background.paper',
      transition: 'all 200ms ease-in-out',
      '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
    }}>
      <CardContent sx={{ p: '12px !important', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '11px',
            fontWeight: 500,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            lineHeight: 1.3,
            mb: '4px',
          }}>
            Net Revenue MTD
          </Typography>
          {loading ? (
            <>
              <Skeleton variant="text" width="65%" height={28} sx={{ mb: 1 }} />
              <Skeleton variant="text" height={14} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" height={14} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" height={14} />
            </>
          ) : (
            <>
              <Typography sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '20px',
                fontWeight: 700,
                color: valueColor,
                lineHeight: 1.1,
                mb: 1,
              }}>
                {formatCurrency(netRevMTD)}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: 'text.secondary' }}>
                  Grok AI
                </Typography>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600 }}>
                  {formatCurrency(grokCostMTD)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: 'text.secondary' }}>
                  AWS Infrastructure
                </Typography>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600 }}>
                  {formatCurrency(awsCostMTD)}
                </Typography>
              </Box>
              <Divider sx={{ mb: 0.75 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700 }}>
                  Total Costs
                </Typography>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700 }}>
                  {formatCurrency(totalCostsMTD)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── New vs churned bar chart ─────────────────────────────────────────────────

const NewVsChurnedChart: React.FC<{
  daily: { date: string; newSubs: number; churned: number }[];
  loading?: boolean;
}> = ({ daily, loading }) => {
  const chartData = (daily.length > 30 ? daily.slice(-30) : daily).map(d => ({
    date: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    new: d.newSubs,
    churned: d.churned,
  }));

  if (loading) {
    return (
      <Card sx={{ height: '100%', width: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width={160} height={20} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', width: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827' }}>
            New vs. Churned (Daily)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: '#0d9488' }} />
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#6B7280' }}>New</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: '#7D1935' }} />
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#6B7280' }}>Churned</Typography>
            </Box>
          </Box>
        </Box>
        {chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
            <Typography color="text.secondary" sx={{ fontSize: '13px' }}>No data available</Typography>
          </Box>
        ) : (
          <Box sx={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barSize={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fontFamily: 'Montserrat, sans-serif', fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 9, fontFamily: 'Montserrat, sans-serif', fill: '#9CA3AF' }}
                  axisLine={false}
                  tickLine={false}
                  width={20}
                />
                <Tooltip
                  contentStyle={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }}
                />
                <Bar dataKey="new" name="New" fill="#0d9488" radius={[2, 2, 0, 0]} />
                <Bar dataKey="churned" name="Churned" fill="#7D1935" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Access denied ────────────────────────────────────────────────────────────

const AccessDenied: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <Paper elevation={0} sx={{ p: 6, maxWidth: 480, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'error.50', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
        <BlockIcon sx={{ fontSize: 36, color: 'error.main' }} />
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Access Restricted</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        You need <strong>revenue_analytics</strong> or <strong>subscription_analytics</strong> permissions to view this page.
      </Typography>
      <Button variant="contained" href="/dashboard/users">Go to User Management</Button>
    </Paper>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const RevenueSubscriptionsPage: React.FC = () => {
  const [range, setRange] = useTimeRangeParam({ type: '30d' });
  const [activeSegment, setActiveSegment] = useState<Segment>('growth');

  const { hasAccess } = usePermissionAccess();
  const canViewRevenue = hasAccess('revenue_analytics');
  const canViewSubscriptions = hasAccess('subscription_analytics');

  // Every endpoint behind this page takes `days=N` and counts BACK FROM NOW —
  // there is no from/to on the wire. An inclusive span is therefore translated
  // to its length, and a custom window that ends in the past is reported
  // honestly below rather than silently re-anchored to today.
  const hookDays = useMemo(() => String(rangeDays(range)), [range]);

  /** True when the operator picked a custom span that does not end today. */
  const customEndsInPast = range.type === 'custom' && range.to < toYmd(new Date());

  const {
    health, anomalySignals, tierBreakdown, growthKpis,
    trendData, costs, loading, refreshing, effectiveDays, refresh,
  } = useIntelligenceData(hookDays);

  if (!canViewRevenue && !canViewSubscriptions) {
    return <AccessDenied />;
  }

  const cashMTD = health.cashCollectedMtd || 0;
  const netRevMTD = cashMTD - costs.totalCostsMTD;

  // Margin is undefined (not 0%) when there's no revenue
  const marginPct = cashMTD > 0 ? (netRevMTD / cashMTD) * 100 : null;
  const marginDisplay = marginPct !== null ? `${marginPct.toFixed(1)}%` : '—';

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page Header ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <MoneyIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Revenue &amp; Subscriptions
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: 'text.secondary' }}>
            Financial performance and subscriber analytics
          </Typography>
        </Box>

        {/* One shared time filter + refresh */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <TimeRangeFilter
            value={range}
            onChange={setRange}
            onRefresh={refresh}
            refreshing={refreshing}
          />

          {/* Say what the numbers actually cover. Every endpoint here counts
              back from now, so a window ending in the past cannot be honoured —
              printing the picked dates would be a lie about the data. */}
          {customEndsInPast && (
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11.5px', color: 'text.secondary', maxWidth: 420, textAlign: 'right', lineHeight: 1.4 }}>
              These figures measure back from today. {formatCustomLabel(range)} is
              {' '}{effectiveDays} days long, so the last {effectiveDays} days —
              ending today — are what is shown.
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── §1 Revenue ───────────────────────────────────────────────── */}
      {canViewRevenue && (
        <>
          <SectionHeader title="Revenue Performance" subtitle="MTD financials and cost breakdown" />

          {/* 5 tiles — stretch to tallest (Net Revenue card) */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'stretch' }}>
            <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
              <MetricCard
                title="MRR"
                value={formatCurrency(health.mrr)}
                icon="revenue"
                color="revenue"
                subtitle={`${health.monthlySinceCutoff} mo · ${health.annualSinceCutoff} ann`}
                loading={loading}
                variant="compact"
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
              <MetricCard
                title="ARR"
                value={formatCurrency(health.arr)}
                icon="revenue"
                color="revenue"
                subtitle="From annual plans"
                loading={loading}
                variant="compact"
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
              <MetricCard
                title="Cash MTD"
                value={formatCurrency(cashMTD)}
                icon="receipt"
                color="revenue"
                subtitle="Actual cash since Apr 1"
                loading={loading}
                variant="compact"
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
              <NetRevenueCostCard
                netRevMTD={netRevMTD}
                grokCostMTD={costs.grokCostMTD}
                awsCostMTD={costs.awsCostMTD}
                totalCostsMTD={costs.totalCostsMTD}
                loading={loading}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
              <MetricCard
                title="Margin MTD"
                value={marginDisplay}
                icon="cost"
                color={marginPct !== null && marginPct >= 0 ? 'revenue' : 'costs'}
                subtitle="Net margin on cash collected"
                loading={loading}
                variant="compact"
              />
            </Box>
          </Box>
        </>
      )}

      {/* ── §2 Subscription Analysis ─────────────────────────────────── */}
      {canViewSubscriptions && (
        <>
          <SectionHeader title="Subscription Analysis" subtitle="Subscriber counts, anomaly signals, and plan mix" />

          <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {[
              { title: 'Since Launch',    value: health.totalNewSinceCutoff, icon: 'subscriptions' as const, color: 'users' as const,  subtitle: 'Active since Apr 1' },
              { title: 'Monthly Plans',   value: health.monthlySinceCutoff,  icon: 'subscriptions' as const, color: 'users' as const,  subtitle: 'Active monthly' },
              { title: 'Annual Plans',    value: health.annualSinceCutoff,   icon: 'subscriptions' as const, color: 'users' as const,  subtitle: 'Active annual' },
              { title: 'New This Period', value: growthKpis.newThisPeriod,   icon: 'users' as const,         color: 'users' as const,  subtitle: `Net new: ${growthKpis.netNew >= 0 ? '+' : ''}${growthKpis.netNew}` },
              { title: '30-Day Churn',    value: `${(health.churnRate30d ?? 0).toFixed(1)}%`, icon: 'cost' as const, color: 'costs' as const, subtitle: `${health.churned30d} churned` },
            ].map(tile => (
              <Box key={tile.title} sx={{ flex: { xs: '1 1 45%', md: '1 1 0' }, minWidth: 0 }}>
                <MetricCard
                  title={tile.title}
                  value={tile.value}
                  icon={tile.icon}
                  color={tile.color}
                  subtitle={tile.subtitle}
                  loading={loading}
                  variant="compact"
                />
              </Box>
            ))}
          </Box>

          {anomalySignals.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <AnomalyStrip signals={anomalySignals} />
            </Box>
          )}

          <Grid container spacing={2} sx={{ mb: 5 }}>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', width: '100%' }}>
              <NewVsChurnedChart daily={trendData.daily} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', width: '100%' }}>
              <Box sx={{ width: '100%' }}>
                <PlanMixDonut tierBreakdown={tierBreakdown} totalActive={health.totalNewSinceCutoff} />
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── §3 Intelligence Strip ────────────────────────────────────── */}
      {canViewSubscriptions && (
        <>
          <SectionHeader title="Subscription Intelligence" subtitle="Deep-dive analytics — select a segment" />

          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {(Object.keys(SEGMENT_LABELS) as Segment[]).map(seg => (
              <Button
                key={seg}
                variant={activeSegment === seg ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setActiveSegment(seg)}
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '12px',
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 2,
                }}
              >
                {SEGMENT_LABELS[seg]}
              </Button>
            ))}
          </Box>

          {activeSegment === 'growth'   && <GrowthTrendsTab   timeRangeDays={effectiveDays} />}
          {activeSegment === 'churn'    && <ChurnDeepDiveTab  timeRangeDays={effectiveDays} />}
          {activeSegment === 'cohorts'  && <CohortsExpandedTab timeRangeDays={effectiveDays} />}
          {activeSegment === 'upgrades' && <UpgradePathsTab   timeRangeDays={effectiveDays} />}
        </>
      )}

      <Box sx={{ mt: 4 }}>
        <DataSourceAttribution
          explainer="Metrics from subscription intelligence and growth-trends endpoints. Costs from Grok analytics + AWS Cost Explorer."
          source="/api/admin/subscriptions/intelligence + /growth-trends + /cost-analytics"
          endpoint="/api/admin/subscriptions/intelligence"
          reportingCutoff="2026-04-01T00:00:00Z"
        />
      </Box>
    </Box>
  );
};

export default RevenueSubscriptionsPage;
